import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Form, toast } from '@qodo/design-system';
import { zodResolver } from '@hookform/resolvers/zod';
import { useProvisionGroupTokenMutation } from 'network/mutations/useProvisionGroupTokenMutation';
import { Step } from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { spawn } from 'utils/translations';
import { prependHttpsIfNeeded } from '../../GithubManifestInstallationDialog/utils';
import { CONFIGURE_TOKEN_FIELDS, FORM_IDS } from '../consts';
import { useGitlabInstallation } from '../gitlabInstallationContext';
import { getConfigureGatTokenFormSchema } from '../schemas';
import { ConfigureGatTokenFormData } from '../types';

/**
 * Group Access Token step: paste a group access token + single group URL,
 * provision the group, advance on success (backend errors surface via toast).
 */
export const ConfigureGatToken = () => {
  const { setIsPending, goNext } = useMultiStepDialog();
  const { setTotalSucceededCount } = useGitlabInstallation();
  const { mutate: provisionGroupToken, isPending } =
    useProvisionGroupTokenMutation();

  const spawnStep = spawn('dialogs.gitlab-installation.configure-token-gat');

  const methods = useForm<ConfigureGatTokenFormData>({
    resolver: zodResolver(getConfigureGatTokenFormSchema()),
    defaultValues: {
      [CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN]: '',
      [CONFIGURE_TOKEN_FIELDS.GITLAB_URL]: '',
    },
  });

  const formClassName =
    "[&_[data-slot='form-label']]:font-mono [&_[data-slot='form-label']]:text-xs [&_[data-slot='form-label']]:leading-[140%] [&_[data-slot='form-description']]:text-text-bg-neutral-mild";

  useEffect(() => {
    setIsPending(isPending);
  }, [isPending]);

  const onSubmit = (formData: ConfigureGatTokenFormData) => {
    provisionGroupToken(
      {
        accessToken: formData.accessToken,
        gitlabUrl: prependHttpsIfNeeded(formData.gitlabUrl),
      },
      {
        // A 4xx is toasted by the mutation. Guard on succeededCount too: the
        // provision loop is best-effort and can return 200 with 0 succeeded, in
        // which case we surface the group's error and stay on the step.
        onSuccess: (result) => {
          setTotalSucceededCount(result.succeededCount);

          if (result.succeededCount > 0) {
            goNext();

            return;
          }

          toast.error(
            result.results[0]?.error ?? spawnStep('provision-failed'),
          );
        },
      },
    );
  };

  return (
    <Step title={spawnStep('title')} description={spawnStep('description')}>
      <Form
        methods={methods}
        onSubmit={onSubmit}
        className={formClassName}
        {...({ id: FORM_IDS.CONFIGURE_TOKEN } as Record<string, string>)}
      >
        <Form.Input
          name={CONFIGURE_TOKEN_FIELDS.GITLAB_URL}
          label={spawnStep('gitlab-url.label')}
          placeholder={spawnStep('gitlab-url.placeholder')}
        />
        <Form.Input
          type="password"
          name={CONFIGURE_TOKEN_FIELDS.ACCESS_TOKEN}
          label={spawnStep('access-token.label')}
          placeholder={spawnStep('access-token.placeholder')}
        />
      </Form>
    </Step>
  );
};
